import { useCallback } from "react";
import { toast } from "react-hot-toast";
import { parseApiError } from "./parseApiError";

/**
 * One-call error handler for react-hook-form + RTK Query.
 *
 *   const { handleApiError } = useApiFormErrors(setError);
 *   try { await mutation(payload).unwrap(); }
 *   catch (err) { handleApiError(err, { toastId }); }
 *
 * It will:
 *   - Bind every backend field error to the matching RHF field (so inputs go red).
 *   - Surface non-field errors as a toast.
 *   - Fall back to a sensible message for network/auth/rate-limit failures.
 */
export function useApiFormErrors(setError) {
  const handleApiError = useCallback(
    (error, options = {}) => {
      const {
        toastId,
        fallbackMessage,
        showToast = true,
        knownFields,
      } = options;

      const parsed = parseApiError(error);
      const { fieldErrors, message, status } = parsed;

      const unmappedMessages = [];

      if (typeof setError === "function") {
        for (const [field, msg] of Object.entries(fieldErrors)) {
          if (knownFields && !knownFields.includes(field)) {
            unmappedMessages.push(msg);
            continue;
          }
          setError(field, { type: "server", message: msg }, { shouldFocus: true });
        }
      } else {
        unmappedMessages.push(...Object.values(fieldErrors));
      }

      const finalMessage =
        unmappedMessages.length > 0
          ? [message, ...unmappedMessages].filter(Boolean).join(" ")
          : message || fallbackMessage;

      if (showToast && finalMessage) {
        toast.error(finalMessage, toastId ? { id: toastId, duration: 5000 } : { duration: 5000 });
      } else if (toastId) {
        // Clear the loading toast even if we suppressed the error toast.
        toast.dismiss(toastId);
      }

      return { ...parsed, message: finalMessage, status };
    },
    [setError]
  );

  return { handleApiError };
}
